import {
  AbstractControl,
  FormArray,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
} from '@angular/forms';
import type {
  TeacherWorkloadMode,
  TeachingRangeOption,
} from 'src/app/services/models/subjects/subject.model';

export interface ParsedTeachingRangeDate {
  date: string;
  dayOfWeek: number;
  startTime: string | null;
  endTime: string | null;
}

function toTeachingRangeText(value: unknown): string {
  if (typeof value === 'string') {
    return value.trim();
  }

  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value).trim();
  }

  return '';
}

function getTeachingRangeSelectionText(value: unknown): string {
  if (value != null && typeof value === 'object') {
    const record = value as Record<string, unknown>;
    const valueText = toTeachingRangeText(record['value']);
    return valueText !== '' ? valueText : toTeachingRangeText(record['label']);
  }

  return toTeachingRangeText(value);
}

function splitTeachingRangeSelection(range: string): string[] {
  if (range === '' || range === '-') {
    return [];
  }

  return range
    .split(/,\s*\n|\n|,\s*(?=วัน)/)
    .map((item) => item.trim())
    .filter((item) => item !== '');
}

export function normalizeTimeOptionValue(value: unknown): string | null {
  if (typeof value !== 'string' || value.trim() === '') {
    return null;
  }

  const match = value.match(/(?:T|\s|^)(\d{1,2}):(\d{2})(?::\d{2})?/);

  if (match == null) {
    return null;
  }

  const hour = Number(match[1]);
  const minute = Number(match[2]);

  return hour >= 0 && hour <= 23 && minute >= 0 && minute <= 59
    ? `${hour}:${match[2]}`
    : null;
}

function normalizeTeachingRangeTime(value: string | undefined): string | null {
  if (value == null) {
    return null;
  }

  return normalizeTimeOptionValue(value);
}

export function normalizeTeachingRangeSelection(value: unknown): string[] {
  const ranges = Array.isArray(value) ? value : [value];

  return ranges
    .flatMap((range) => splitTeachingRangeSelection(getTeachingRangeSelectionText(range)))
    .filter((range) => range !== '');
}

export function parseTeachingRangeValue(value?: string | null): string[] {
  return value == null || value === '-' ? [] : normalizeTeachingRangeSelection(value);
}

export function formatSelectedTeachingRanges(value: unknown): string {
  const ranges = normalizeTeachingRangeSelection(value);
  return ranges.length > 0 ? ranges.join(',\n') : '-';
}

export function parseTeachingRangeIsoDate(range: string): ParsedTeachingRangeDate | null {
  const displayDateMatch = range.match(/(\d{2})\/(\d{2})\/(\d{4})/);
  const isoDateMatch = range.match(/(?:^|\D)(\d{4})-(\d{2})-(\d{2})(?:\D|$)/);
  const timeMatch = range.match(/\((\d{1,2}:\d{2})\s*-\s*(\d{1,2}:\d{2})\)/);

  if (displayDateMatch == null && isoDateMatch == null) {
    return null;
  }

  const day = Number(displayDateMatch?.[1] ?? isoDateMatch?.[3]);
  const month = Number(displayDateMatch?.[2] ?? isoDateMatch?.[2]);
  const rawYear = Number(displayDateMatch?.[3] ?? isoDateMatch?.[1]);
  const year = rawYear > 2400 ? rawYear - 543 : rawYear;
  const date = new Date(year, month - 1, day);

  if (Number.isNaN(date.getTime())) {
    return null;
  }

  return {
    date: `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`,
    dayOfWeek: date.getDay(),
    startTime: normalizeTeachingRangeTime(timeMatch?.[1]),
    endTime: normalizeTeachingRangeTime(timeMatch?.[2]),
  };
}

export function getTeachingRangeIdentity(range: string): string {
  const parsedRange = parseTeachingRangeIsoDate(range);

  if (parsedRange == null) {
    return range.trim();
  }

  return parsedRange.startTime != null && parsedRange.endTime != null
    ? `${parsedRange.date}|${parsedRange.startTime}|${parsedRange.endTime}`
    : parsedRange.date;
}

export function teachingRangeMatchesTime(
  range: string,
  dayOfWeek: number,
  startTime: string,
  endTime: string
): boolean {
  const parsedRange = parseTeachingRangeIsoDate(range);

  if (parsedRange?.dayOfWeek !== dayOfWeek) {
    return false;
  }

  return parsedRange.startTime == null || parsedRange.endTime == null
    || (
      parsedRange.startTime === normalizeTeachingRangeTime(startTime)
      && parsedRange.endTime === normalizeTeachingRangeTime(endTime)
    );
}

export function buildAvailableTeacherTeachingRangeOptions(
  options: TeachingRangeOption[],
  selectionsByRow: unknown[],
  _workloadMode: TeacherWorkloadMode
): TeachingRangeOption[][] {
  return selectionsByRow.map(() => options);
}

export function parseDateOnly(value?: string | null): Date | null {
  if (value == null || value === '') {
    return null;
  }

  const [year, month, day] = value.split('-').map(Number);

  if (!year || !month || !day) {
    return null;
  }

  const date = new Date(year, month - 1, day);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function formatTeachingDate(value: Date): string {
  const day = String(value.getDate()).padStart(2, '0');
  const month = String(value.getMonth() + 1).padStart(2, '0');
  const year = value.getFullYear() + 543;

  return `${day}/${month}/${year}`;
}

/**
 * Midterm/final exam windows for a term. Dates are ISO `YYYY-MM-DD` strings (or
 * empty/null when unset). Mirrors the backend `AcademicTerm#during_exam_period?`.
 */
export interface ExamPeriodWindows {
  midtermStartDate?: string | null;
  midtermEndDate?: string | null;
  finalStartDate?: string | null;
  finalEndDate?: string | null;
}

function isWithinDateRange(date: Date, start?: string | null, end?: string | null): boolean {
  const startDate = parseDateOnly(start);
  const endDate = parseDateOnly(end);

  if (startDate == null || endDate == null) {
    return false;
  }

  return date >= startDate && date <= endDate;
}

/**
 * True when `date` falls inside the midterm or final exam window (inclusive of
 * both bounds). A window with a missing bound is treated as no exclusion, so the
 * result degrades safely to `false` — matching the backend `in_exam_range?`.
 */
export function isExamPeriodDate(date: Date, windows?: ExamPeriodWindows | null): boolean {
  if (windows == null) {
    return false;
  }

  return (
    isWithinDateRange(date, windows.midtermStartDate, windows.midtermEndDate)
    || isWithinDateRange(date, windows.finalStartDate, windows.finalEndDate)
  );
}

/**
 * True when a formatted teaching-range label (e.g. already-selected/fallback
 * ranges carried over from a previous save) falls inside an exam window.
 * Unparseable labels are never treated as exam-period dates.
 */
export function isExamPeriodRange(range: string, windows?: ExamPeriodWindows | null): boolean {
  const parsedRange = parseTeachingRangeIsoDate(range);
  const date = parsedRange == null ? null : parseDateOnly(parsedRange.date);

  return date != null && isExamPeriodDate(date, windows);
}

export function countDayOfWeekOccurrences(
  startDate: Date,
  endDate: Date,
  dayOfWeek: number,
  examWindows?: ExamPeriodWindows | null
): number {
  const currentDate = new Date(startDate);
  let count = 0;

  while (currentDate <= endDate) {
    if (currentDate.getDay() === dayOfWeek && !isExamPeriodDate(currentDate, examWindows)) {
      count += 1;
    }

    currentDate.setDate(currentDate.getDate() + 1);
  }

  return count;
}

export function toNullableString(value: unknown): string | null {
  if (value == null) {
    return null;
  }

  const text = typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean'
    ? String(value).trim()
    : '';
  return text === '' ? null : text;
}

export function toNumberOrZero(value: unknown): number {
  const numberValue = Number(value ?? 0);
  return Number.isFinite(numberValue) ? numberValue : 0;
}

export function toNumberOrNull(value: unknown): number | null {
  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? numberValue : null;
}

// Preview only. The backend recomputes the authoritative section workload.
const WORKLOAD_WEEKS = 15;
const PRACTICAL_WORKLOAD_HOURS_PER_CREDIT = 30;
const COURSEUNIT_PATTERN =
  /^\s*(\d+(?:\.\d+)?)\s*(?:\(\s*(\d+(?:\.\d+)?)\s*-\s*(\d+(?:\.\d+)?)\s*-\s*(\d+(?:\.\d+)?)\s*\))?\s*$/;

export function deriveTeachingWorkloadHoursFromCourseunit(
  courseunit?: string | null,
  teachingTypeCode = 'LECTURE',
): number {
  const value = String(courseunit ?? '').trim();
  if (value === '') {
    return 0;
  }

  const match = COURSEUNIT_PATTERN.exec(value);
  if (match == null) {
    return 0;
  }

  const lectureCredits = Number(match[2]) || 0;
  const practicalHours = Number(match[3]) || 0;
  if (teachingTypeCode === 'LECTURE') {
    return lectureCredits * WORKLOAD_WEEKS;
  }
  if (teachingTypeCode === 'LABORATORY') {
    return Math.ceil(practicalHours / 3) * PRACTICAL_WORKLOAD_HOURS_PER_CREDIT;
  }

  return 0;
}

export function toMinute(value: unknown): number | null {
  if (typeof value !== 'string') {
    return null;
  }

  const [hour, minute] = value.split(':').map(Number);

  if (!Number.isFinite(hour) || !Number.isFinite(minute)) {
    return null;
  }

  return hour * 60 + minute;
}

export function teachingTimesValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const teachingTimes = control as FormArray<FormGroup>;
    const ranges = teachingTimes.controls
      .map((time, index) => {
        const teachingDay = toNullableString(time.get('teachingDay')?.value);
        const start = toMinute(time.get('startTime')?.value);
        const end = toMinute(time.get('endTime')?.value);

        if (teachingDay === null || start === null || end === null) {
          return null;
        }

        return { end, index, start, teachingDay };
      })
      .filter((range): range is { end: number; index: number; start: number; teachingDay: string } => range != null);

    if (ranges.some((range) => range.start >= range.end)) {
      return { timeOrder: true };
    }

    for (let i = 0; i < ranges.length; i += 1) {
      for (let j = i + 1; j < ranges.length; j += 1) {
        const current = ranges[i];
        const next = ranges[j];

        if (
          current.teachingDay === next.teachingDay
          && current.start < next.end
          && next.start < current.end
        ) {
          return { timeOverlap: true };
        }
      }
    }

    return null;
  };
}

export function getCurrentBuddhistYear(): number {
  return new Date().getFullYear() + 543;
}

export function toBuddhistYear(year: number): number {
  return year > 2400 ? year : year + 543;
}

export function toChristianYear(value: unknown): string | null {
  const year = Number(value);
  return Number.isFinite(year) ? String(year > 2400 ? year - 543 : year) : null;
}
